import React from 'react';
import { Star } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { generateWebPWithFallback } from '@/utils/imageUtils';
import { ScrollReveal } from '@/components/ui/ScrollReveal';

export interface Review {
  id: string;
  authorName: string;
  authorRole?: string;
  authorAvatarUrl?: string;
  content: string;
  rating: number;
}

export interface ReviewsBlockProps {
  title: string;
  subtitle?: string;
  reviews: Review[];
  layout?: 'grid' | 'carousel';
}

export const ReviewsBlock: React.FC<ReviewsBlockProps> = ({
  title,
  subtitle,
  reviews = [],
  layout = 'grid',
}) => {
  if (reviews.length === 0) return null;

  return (
    <section className="py-16 md:py-24 bg-muted/30">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal animation="up" className="text-center max-w-3xl mx-auto mb-16">
          {subtitle && (
            <span className="text-[var(--brand-primary)] font-semibold text-sm uppercase tracking-wider mb-2 block">
              {subtitle}
            </span>
          )}
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            {title}
          </h2>
        </ScrollReveal>

        {/* Grid de avaliações com entrada suave escalonada */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reviews.map((review, idx) => (
            <ScrollReveal key={review.id} animation="up" delayMs={idx * 100}>
              <Card className="border-border/50 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 bg-background h-full">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-6">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`h-5 w-5 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'fill-muted text-muted'
                          }`}
                      />
                    ))}
                  </div>

                  <blockquote className="text-lg text-foreground mb-8 line-clamp-4">
                    "{review.content}"
                  </blockquote>

                  <div className="flex items-center gap-4">
                    {review.authorAvatarUrl ? (
                      <img
                        src={generateWebPWithFallback(review.authorAvatarUrl, 85, 100, 100).webp}
                        alt={review.authorName}
                        className="h-12 w-12 rounded-full object-cover"
                        loading="lazy"
                        decoding="async"
                        width="48"
                        height="48"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-[var(--brand-primary)]/10 flex items-center justify-center text-[var(--brand-primary)] font-bold text-lg">
                        {review.authorName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-foreground">
                        {review.authorName}
                      </div>
                      {review.authorRole && (
                        <div className="text-sm text-muted-foreground">
                          {review.authorRole}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};
